import { useCallback, useEffect, useMemo, useState } from "react";
import {
  CalendarDays,
  CalendarPlus,
  ChartColumn,
  ClipboardList,
  LayoutDashboard,
  PencilLine,
  Settings,
  Users,
  type LucideIcon,
} from "lucide-react";
import { Loader } from "@/components/ui/Loader";
import { useAuth } from "@/hooks/useAuth";
import { apiError, fetchManutencao, type ManutencaoSnapshot } from "@/services/manutencao";
import { cn } from "@/utils/cn";
import { AdminPanels } from "./AdminPanels";
import { OperacaoPanels } from "./OperacaoPanels";

type TabId =
  | "painel"
  | "servicos"
  | "livre"
  | "agendamentos"
  | "novo"
  | "equipe"
  | "ponto"
  | "relatorios"
  | "config";

const TABS: Array<{ id: TabId; label: string; icon: LucideIcon }> = [
  { id: "painel", label: "Painel", icon: LayoutDashboard },
  { id: "servicos", label: "Meus serviços", icon: ClipboardList },
  { id: "livre", label: "Sem agendamento", icon: PencilLine },
  { id: "agendamentos", label: "Agendamentos", icon: CalendarDays },
  { id: "novo", label: "Novo agendamento", icon: CalendarPlus },
  { id: "equipe", label: "Equipe", icon: Users },
  { id: "ponto", label: "Folha de ponto", icon: CalendarDays },
  { id: "relatorios", label: "Relatórios", icon: ChartColumn },
  { id: "config", label: "Configurações", icon: Settings },
];

export function ManutencaoPage() {
  const { user } = useAuth();
  const [tab, setTab] = useState<TabId>("painel");
  const [data, setData] = useState<ManutencaoSnapshot | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    setError(null);
    try {
      setData(await fetchManutencao());
    } catch (err) {
      setError(apiError(err, "Não foi possível carregar a manutenção."));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  const meuNome = user?.displayName ?? "";
  const executores = useMemo(
    () => (data?.colaboradores ?? []).filter((c) => c.status === "aprovado"),
    [data],
  );

  return (
    <div className="flex min-h-full flex-col gap-4">
      <header>
        <h1 className="text-2xl font-bold text-slate-900">Manutenção</h1>
        <p className="mt-1 text-sm text-slate-500">
          Agenda de visitas, serviços do dia, equipe e relatórios das obras.
        </p>
      </header>

      <div className="flex gap-2 overflow-x-auto pb-1">
        {TABS.map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            type="button"
            onClick={() => setTab(id)}
            className={cn(
              "inline-flex h-11 shrink-0 items-center gap-2 rounded-xl border px-3 text-sm font-semibold transition-colors",
              tab === id
                ? "border-brand-600 bg-brand-600 text-white"
                : "border-slate-200 bg-white text-slate-600 hover:border-brand-200 hover:text-brand-700",
            )}
          >
            <Icon className="h-4 w-4" />
            {label}
          </button>
        ))}
      </div>

      {error && (
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>
      )}

      {loading || !data ? (
        <div className="flex flex-1 items-center justify-center py-16">
          <Loader size="md" label="Carregando manutenção…" />
        </div>
      ) : (
        <div className="min-h-0 flex-1">
          {tab === "equipe" || tab === "ponto" || tab === "relatorios" || tab === "config" ? (
            <AdminPanels tab={tab} data={data} reload={reload} />
          ) : (
            <OperacaoPanels tab={tab} data={data} reload={reload} meuNome={meuNome} executores={executores} onGo={setTab} />
          )}
        </div>
      )}
    </div>
  );
}
